import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RoleBadge } from "@/components/common/Badges";
import { label, when } from "@/lib/trust";

function PermissionEditor({ row, grantable, onDone }) {
  const [grants, setGrants] = useState(row.grants);
  const [revokes, setRevokes] = useState(row.revokes);
  const [reason, setReason] = useState("");
  const toggle = (list, set, p) => set(list.includes(p) ? list.filter((x) => x !== p) : [...list, p]);
  const save = async () => {
    try { await api.put(`/staff/users/${row.user_id}/permissions`, { grants, revokes, reason }); toast.success("Permissions updated"); onDone(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="space-y-3 rounded border border-[#2A3447] p-3" data-testid={`perm-editor-${row.username}`}>
      <p className="text-xs text-slate-400">Extra grants (e.g. <strong>appeals.decide</strong> = independent appeals team). Owner-only powers cannot be granted.</p>
      <div className="flex flex-wrap gap-3">{grantable.filter((p) => !row.role_permissions.includes(p)).map((p) => (
        <label key={p} className="flex items-center gap-1 text-xs"><input type="checkbox" checked={grants.includes(p)} onChange={() => toggle(grants, setGrants, p)} data-testid={`grant-${row.username}-${p}`} />{p}</label>))}</div>
      <p className="text-xs text-slate-400">Revoke role permissions (least privilege):</p>
      <div className="flex flex-wrap gap-3">{row.role_permissions.map((p) => (
        <label key={p} className="flex items-center gap-1 text-xs"><input type="checkbox" checked={revokes.includes(p)} onChange={() => toggle(revokes, setRevokes, p)} data-testid={`revoke-${row.username}-${p}`} />{p}</label>))}</div>
      <div className="flex gap-2"><input className="field flex-1" placeholder="Reason (recorded in the audit log)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`perm-reason-${row.username}`} />
        <button className="btn-gold" onClick={save} data-testid={`perm-save-${row.username}`}>Save</button></div>
    </div>
  );
}

function Activity({ userId }) {
  const { data } = useQuery({ queryKey: ["staff", "activity", userId], queryFn: () => api.get(`/staff/activity/${userId}`).then((r) => r.data) });
  if (!data) return null;
  return (
    <div className="max-h-64 space-y-1 overflow-y-auto rounded border border-[#2A3447] p-3 font-mono text-[11px] text-slate-400" data-testid={`activity-${userId}`}>
      {data.discipline.map((d) => <p key={d.discipline_id} className="text-red-300">DISCIPLINE {d.action} · {when(d.created_at)} · {d.reason}</p>)}
      {data.items.map((a) => <p key={a.audit_id}>{when(a.created_at)} · <span className="text-amber-200">{a.action}</span> · {a.target_type}:{a.target_id}{a.reason && ` · ${a.reason}`}</p>)}
      {!data.items.length && <p>No recorded actions.</p>}
    </div>
  );
}

function Discipline({ row, onDone }) {
  const act = async (action) => {
    const reason = window.prompt(`Reason for ${action} of @${row.username} (min 10 characters):`);
    if (!reason) return;
    try { await api.post(`/staff/users/${row.user_id}/discipline`, { action, reason }); toast.success(`Recorded: ${action}`); onDone(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return <div className="flex gap-2">{["warning", "demote", "suspend"].map((a) => <button key={a} className="btn-outline text-xs" onClick={() => act(a)} data-testid={`discipline-${a}-${row.username}`}>{a}</button>)}</div>;
}

function StaffRow({ row, grantable, refresh }) {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const w = row.workload;
  return (
    <div className="panel space-y-3 p-4" data-testid={`staff-row-${row.username}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><p className="text-slate-100">{row.display_name} <span className="font-mono text-xs text-slate-500">@{row.username}</span></p>
          <div className="mt-1 flex flex-wrap items-center gap-2"><RoleBadge role={row.role} />{row.appeals_team && <span className="font-mono text-[10px] uppercase text-emerald-300" data-testid={`appeals-team-${row.username}`}>Appeals team</span>}
            {row.account_status !== "active" && <span className="font-mono text-[10px] uppercase text-red-300">{row.account_status}</span>}</div></div>
        <p className="font-mono text-xs text-slate-400">Apps {w.applications} · Cases {w.cases} · Appeals {w.appeals} · Spot checks {w.spot_checks}</p>
        {(row.permissions || can("staff.activity")) && <button className="btn-ghost text-xs" onClick={() => setOpen(!open)} data-testid={`staff-row-toggle-${row.username}`}>{open ? "Hide" : "Manage"}</button>}
      </div>
      {open && <>
        {row.permissions && <p className="font-mono text-[11px] text-slate-500">Effective: {row.permissions.join(", ")}</p>}
        {row.editable && <PermissionEditor row={row} grantable={grantable} onDone={refresh} />}
        {can("staff.discipline") && row.role !== "site_owner" && <Discipline row={row} onDone={refresh} />}
        {can("staff.activity") && <Activity userId={row.user_id} />}
      </>}
    </div>
  );
}

export function Directory() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["staff", "directory"], queryFn: () => api.get("/staff/directory").then((r) => r.data) });
  return (
    <div className="space-y-3" data-testid="staff-directory">
      <p className="text-sm text-slate-500">Platform staff only. Community and league roles are managed inside each organisation and never grant platform powers. Assign or revoke roles from the Users tab; {label("head_administrator")} and the owner's powers can only be granted by the owner.</p>
      {data?.items.map((r) => <StaffRow key={r.user_id} row={r} grantable={data.grantable} refresh={() => qc.invalidateQueries({ queryKey: ["staff"] })} />)}
    </div>
  );
}
